import React, { useState, useRef } from "react"
import { Button, Typography, Box } from "@mui/material"
import "./DragDropFile.css"
import { useTranslation } from "react-i18next"

// drag drop file component
const DragDropFile = ({
  label,
  description,
  accept,
  multiple = true,
  onFiles,
  height = 140,
  files = [],
  disabled = false
}) => {
  // drag state
  const [dragActive, setDragActive] = useState(false)

  const { t } = useTranslation()
  // ref
  const inputRef = useRef(null)

  const resolvedLabel = label || t('dragDrop.defaultLabel')

  // triggers the input when the button is clicked
  const trigger = (e) => {
    e?.stopPropagation()
    inputRef.current?.click()
  }

  // handle drag events
  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (["dragenter", "dragover"].includes(e.type)) setDragActive(true)
    if (e.type === "dragleave") setDragActive(false)
  }

  // triggers when file is dropped
  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer?.files?.length) {
      onFiles(Array.from(e.dataTransfer.files))
    }
  }

  // triggers when file is selected with click
  const handleChange = (e) => {
    if (e.target.files?.length) {
      onFiles(Array.from(e.target.files))
    }
  }

  const borderColor = dragActive ? "#1976d2" : "#bbb"

  if (disabled) {
    return (
      <Box
        sx={{
          border: `2px dashed ${borderColor}`,
          borderRadius: 2,
          p: 2,
          position: "relative",
          textAlign: "center",
          height,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          bgcolor: "action.disabledBackground",
          color: "text.disabled"
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{resolvedLabel}</Typography>
        {description && (
          <Typography variant="caption" sx={{ mb: 1, opacity: 0.7 }}>
            {description}
          </Typography>
        )}
        <Button variant="outlined" size="small" disabled>
          {t('dragDrop.browse')}
        </Button>
        {files?.length > 0 && (
          <Typography variant="caption" sx={{ mt: 1 }}>
            {t('dragDrop.filesSelected', { count: files.length })}
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <Box
      // id="form-file-upload"
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      sx={{
        border: `2px dashed ${borderColor}`,
        borderRadius: 2,
        p: 2,
        position: "relative",
        textAlign: "center",
        height,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        cursor: "pointer",
        bgcolor: dragActive ? "action.hover" : "background.paper"
      }}
      onClick={trigger}
    >
      <input
        ref={inputRef}
        type="file"
        style={{ display: "none" }}
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
      />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{resolvedLabel}</Typography>
      {description && (
        <Typography variant="caption" sx={{ mb: 1, opacity: 0.7 }}>
          {description}
        </Typography>
      )}
      <Button variant="outlined" size="small" onClick={trigger}>
        {t('dragDrop.browse')}
      </Button>
      {files?.length > 0 && (
        <Typography variant="caption" sx={{ mt: 1 }}>
          {t('dragDrop.filesSelected', { count: files.length })}
        </Typography>
      )}
    </Box>
  )
}

export default DragDropFile